import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import type { Map as MapboxMap } from 'mapbox-gl';
import { NJText } from '@engie-group/fluid-design-system-react';
import { getMapboxToken, UMMap, type UMMapHandle, type UMMapProps } from '../../urbanmap/_components';

export const ProjectMap = forwardRef<UMMapHandle, UMMapProps>(function ProjectMap(props, ref) {
  const inner = useRef<UMMapHandle>(null);
  const [error, setError] = useState('');
  const hasToken = Boolean(getMapboxToken());
  useImperativeHandle(ref, () => ({
    getMap: () => inner.current?.getMap() ?? null,
    zoomIn: () => inner.current?.zoomIn(),
    zoomOut: () => inner.current?.zoomOut(),
    flyTo: (center, zoom) => inner.current?.flyTo(center, zoom),
    fitBounds: (bounds, padding) => inner.current?.fitBounds(bounds, padding),
  }));
  useEffect(() => {
    if (!hasToken) return;
    let frame = 0;
    let attached: MapboxMap | null = null;
    const onError = (event: { error: Error }) => setError(`Carte indisponible : ${event.error.message}`);
    const attach = () => {
      const map = inner.current?.getMap();
      if (!map) { frame = requestAnimationFrame(attach); return; }
      attached = map; map.on('error', onError);
    };
    frame = requestAnimationFrame(attach);
    return () => { cancelAnimationFrame(frame); attached?.off('error', onError); };
  }, [hasToken]);
  // Mapbox GL 3.26 requires authentication even for UMMap's local style.
  // Do not render a success-shaped blank map or bypass that authentication.
  if (!hasToken) return <div className="sp-map-unavailable" role="status">
    <NJText as="div"><strong>Carte non disponible dans cet environnement</strong></NJText>
    <NJText>Le composant cartographique de référence nécessite un token Mapbox valide avec la version installée.</NJText>
    <NJText>Configurez VITE_MAPBOX_TOKEN dans app/front/.env.local puis redémarrez Vite pour activer le snapping, les centrales et le dessin manuel.</NJText>
    <NJText>Les règles par tronçon restent accessibles dans la liste. La génération simulée, le phasage et le recalcul restent utilisables sans carte.</NJText>
  </div>;
  return <>
    <UMMap {...props} ref={inner} />
    {error && <div className="sp-map-unavailable sp-error" role="alert"><NJText>{error}</NJText></div>}
  </>;
});
